// Hero vignette — a 7-node Feature Factory graph with ambient pulses traveling
// along edges. Pure SVG + a single RAF loop so it's cheap on the marketing
// page. Respects prefers-reduced-motion.

import { useEffect, useState } from "react";

const W = 1264;
const H = 460;
const NW = 188;
const NH = 76;

type NodeType = "agent" | "cond" | "tool" | "human";
interface VNode {
  id: string;
  type: NodeType;
  x: number;
  y: number;
  title: string;
  sub: string;
}

const NODES: VNode[] = [
  {
    id: "spec",
    type: "agent",
    x: 40,
    y: 200,
    title: "Spec Writer",
    sub: "PRD → stories",
  },
  {
    id: "arch",
    type: "agent",
    x: 250,
    y: 200,
    title: "Architect",
    sub: "System design",
  },
  {
    id: "split",
    type: "cond",
    x: 460,
    y: 200,
    title: "Parallel",
    sub: "Fan out",
  },
  {
    id: "coder",
    type: "agent",
    x: 660,
    y: 70,
    title: "Code Generator",
    sub: "Writes impl",
  },
  {
    id: "tests",
    type: "agent",
    x: 660,
    y: 330,
    title: "Test Writer",
    sub: "Unit + e2e",
  },
  {
    id: "sandbox",
    type: "tool",
    x: 870,
    y: 200,
    title: "Sandbox",
    sub: "Run tests on impl",
  },
  {
    id: "gate",
    type: "human",
    x: 1070,
    y: 200,
    title: "Review Gate",
    sub: "Approve validated work",
  },
];

// Coder + Tests both feed into Sandbox so the impl is exercised against the
// generated tests *before* a human ever sees it; only validated work reaches
// the review gate. This mirrors what you'd actually want from such a flow.
const EDGES: [string, string][] = [
  ["spec", "arch"],
  ["arch", "split"],
  ["split", "coder"],
  ["split", "tests"],
  ["coder", "sandbox"],
  ["tests", "sandbox"],
  ["sandbox", "gate"],
];

const HUE: Record<NodeType, number> = {
  agent: 268,
  cond: 35,
  tool: 175,
  human: 340,
};

function portOut(n: VNode) {
  return { x: n.x + NW, y: n.y + NH / 2 };
}
function portIn(n: VNode) {
  return { x: n.x, y: n.y + NH / 2 };
}
// Cubic Bezier control points for the connector between two nodes. Used by
// both the rendered path and the pulse so the dot rides whatever curve the
// connector draws.
function edgeControls(a: VNode, b: VNode) {
  const p = portOut(a);
  const q = portIn(b);
  const dx = Math.max(40, Math.abs(q.x - p.x) * 0.5);
  return {
    p0: p,
    p1: { x: p.x + dx, y: p.y },
    p2: { x: q.x - dx, y: q.y },
    p3: q,
  };
}
function edgePath(a: VNode, b: VNode) {
  const { p0, p1, p2, p3 } = edgeControls(a, b);
  return `M ${p0.x} ${p0.y} C ${p1.x} ${p1.y}, ${p2.x} ${p2.y}, ${p3.x} ${p3.y}`;
}
function bezierAt(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  t: number,
) {
  const u = 1 - t;
  const uu = u * u;
  const tt = t * t;
  return {
    x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
    y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y,
  };
}

export default function HeroVignette() {
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduced) return;
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      setPulse(((t - start) / 2400) % 1);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const byId = Object.fromEntries(NODES.map((n) => [n.id, n]));

  return (
    <div
      className="relative w-full overflow-hidden rounded-[18px] border border-border bg-surface shadow-[0_1px_0_rgba(255,255,255,0.5)_inset,0_18px_50px_-20px_rgba(20,20,30,0.18)]"
      style={{ height: H }}
    >
      {/* Dot grid background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(rgba(20,20,30,0.10) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />
      {/* Soft accent glow under center */}
      <div
        className="pointer-events-none absolute"
        style={{
          left: "38%",
          top: "50%",
          width: 520,
          height: 320,
          transform: "translate(-50%,-50%)",
          background:
            "radial-gradient(closest-side, var(--accent-ring), transparent 70%)",
          filter: "blur(8px)",
          opacity: 0.7,
        }}
      />

      <svg className="absolute inset-0" width={W} height={H} aria-hidden>
        <defs>
          <marker
            id="hero-arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(20,20,30,0.5)" />
          </marker>
        </defs>
        {EDGES.map(([fromId, toId], i) => {
          const a = byId[fromId];
          const b = byId[toId];
          const t = (pulse + i * 0.13) % 1;
          const { p0, p1, p2, p3 } = edgeControls(a, b);
          const { x, y } = bezierAt(p0, p1, p2, p3, t);
          const opacity = Math.sin(t * Math.PI);
          return (
            <g key={i}>
              <path
                d={edgePath(a, b)}
                fill="none"
                stroke="rgba(20,20,30,0.3)"
                strokeWidth="1.5"
                markerEnd="url(#hero-arrow)"
              />
              <circle
                cx={x}
                cy={y}
                r="3"
                fill="var(--accent)"
                opacity={opacity * 0.9}
              />
            </g>
          );
        })}
      </svg>

      {NODES.map((n) => (
        <HeroNode key={n.id} node={n} />
      ))}

      {/* Floating UI chrome to imply a real product */}
      <div className="absolute left-4 top-4 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.04em] text-muted">
        <span
          className="h-[7px] w-[7px] rounded-full"
          style={{ background: "var(--success)" }}
        />
        FEATURE FACTORY · AUTOSAVED
      </div>
      <div className="absolute right-4 top-4 flex h-7 items-center gap-1 rounded-md border border-border bg-surface-2 p-0.5">
        {["−", "72%", "+"].map((l) => (
          <div
            key={l}
            className="num flex h-[22px] min-w-[24px] items-center justify-center px-1.5 text-[11px] text-ink-2"
          >
            {l}
          </div>
        ))}
      </div>
    </div>
  );
}

function HeroNode({ node }: { node: VNode }) {
  const h = HUE[node.type];
  const soft = `oklch(0.96 0.04 ${h})`;
  const text = `oklch(0.32 0.10 ${h})`;
  const bar = `oklch(0.62 0.16 ${h})`;
  const isCond = node.type === "cond";
  const isTool = node.type === "tool";
  const isHuman = node.type === "human";
  const isRect = !isCond && !isTool && !isHuman;

  return (
    <div
      className="absolute"
      style={{
        left: node.x,
        top: node.y,
        width: NW,
        height: NH,
        filter: "drop-shadow(0 1px 1px rgba(20,20,30,0.06))",
      }}
    >
      <svg
        className="absolute inset-0"
        width={NW}
        height={NH}
        style={{ overflow: "visible" }}
        aria-hidden
      >
        {isRect && (
          <rect
            x="1"
            y="1"
            width={NW - 2}
            height={NH - 2}
            rx="10"
            fill="hsl(var(--surface))"
            stroke="hsl(36 24% 88%)"
            strokeWidth="1"
          />
        )}
        {isCond && (
          <polygon
            points={`${NW / 2},2 ${NW - 2},${NH / 2} ${NW / 2},${NH - 2} 2,${NH / 2}`}
            fill={soft}
            stroke="hsl(36 24% 88%)"
            strokeWidth="1"
          />
        )}
        {isTool && (
          <polygon
            points={`14,2 ${NW - 14},2 ${NW - 2},${NH / 2} ${NW - 14},${NH - 2} 14,${NH - 2} 2,${NH / 2}`}
            fill={soft}
            stroke="hsl(36 24% 88%)"
            strokeWidth="1"
          />
        )}
        {isHuman && (
          <rect
            x="1"
            y="1"
            width={NW - 2}
            height={NH - 2}
            rx={NH / 2 - 1}
            fill={soft}
            stroke="hsl(36 24% 88%)"
            strokeWidth="1"
          />
        )}
        <circle cx="0" cy={NH / 2} r="4" fill={bar} />
        <circle cx={NW} cy={NH / 2} r="4" fill={bar} />
      </svg>

      {isRect ? (
        <div className="absolute inset-0 flex flex-col">
          <div
            className="flex h-[26px] items-center gap-1.5 rounded-t-[9px] px-3 text-[11px] font-semibold tracking-[0.01em]"
            style={{ background: soft, color: text }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: bar }}
            />
            {node.title}
            <span className="ml-auto text-[9px] font-medium uppercase tracking-[0.08em] opacity-70">
              Agent
            </span>
          </div>
          <div className="flex flex-1 items-center px-3 py-1.5">
            <div>
              <div className="text-[11px] leading-[1.2] text-muted">
                {node.sub}
              </div>
              <div className="mt-0.5 font-mono text-[10px] tracking-[0.02em] text-muted">
                claude-sonnet-4 · ~3.8k
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-center"
          style={{ color: text, padding: isHuman ? "0 26px" : "0 20px" }}
        >
          <div className="text-[9px] font-semibold uppercase tracking-[0.08em] opacity-85">
            {isCond ? "Conditional" : isTool ? "Tool" : "Human"}
          </div>
          <div className="mt-px text-[12.5px] font-semibold text-ink">
            {node.title}
          </div>
          <div className="mt-px text-[10.5px] text-muted">{node.sub}</div>
        </div>
      )}
    </div>
  );
}
